import type { ProductVariantDto } from "../../../../api";
import { GatedPrice } from "../../../../components/common/gated-price";
import { Text } from "../../../../components/common/typography";
import { getVariantUnitPrice } from "../../utils/card-props";

interface Props {
  products: ProductVariantDto[];
}

export function RuleBasedComparisonRecommendation({ products }: Props) {
  if (products.length < 2) return null;

  // Only variants with a real price take part in the price verdict — an
  // unpriced one would otherwise "win" as the cheapest at QAR 0.
  const priced = products
    .map((product) => ({ product, price: getVariantUnitPrice(product) }))
    .filter((entry): entry is { product: ProductVariantDto; price: number } =>
      entry.price != null,
    );
  // A "best price" needs at least two prices to compare.
  const canComparePrices = priced.length >= 2;
  const cheapest = canComparePrices
    ? priced.reduce((a, b) => (a.price < b.price ? a : b))
    : null;
  const priciest = canComparePrices
    ? priced.reduce((a, b) => (a.price > b.price ? a : b))
    : null;
  const mostWarranty = products.reduce((a, b) => {
    const aw = a.warranty?.name ?? "";
    const bw = b.warranty?.name ?? "";
    return aw >= bw ? a : b;
  });
  const bestStocked = products.reduce((a, b) =>
    Number(a.supplierStock ?? 0) >= Number(b.supplierStock ?? 0) ? a : b,
  );

  const warrantyDiffers = products.some(
    (p) => p.warranty?.name !== products[0].warranty?.name,
  );

  const summary =
    !cheapest || !priciest ? (
      "Pricing isn't available for every product in this comparison — compare warranty coverage and stock availability, or request a quotation for exact pricing."
    ) : cheapest.price === priciest.price ? (
      `${cheapest.product.name ?? "—"} offers the best overall value at the same price point — compare warranty coverage and stock availability to decide which suits your needs best.`
    ) : (
      <>
        {`${priciest.product.name ?? "—"} costs more${warrantyDiffers ? `, with ${mostWarranty.warranty?.name ?? "longer"} warranty coverage` : ""}. ${cheapest.product.name ?? "—"} offers the best value at `}
        <GatedPrice>{formatUnitPrice(cheapest.price)}</GatedPrice>.
      </>
    );

  return (
    <div className="rounded-2xl border border-borderDefault bg-bgWhite p-6 shadow-[0px_2px_12px_0px_rgba(190,189,217,0.2)]">
      <Text variant="capsSm" tone="muted" className="mb-2">
        QUICK COMPARISON
      </Text>
      <Text variant="body" tone="body" className="mb-5">
        {summary}
      </Text>
      <Text variant="bodySmBold" tone="dark" className="mb-3">
        At a glance:
      </Text>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="rounded-xl border border-borderDefault bg-bgLight p-4">
          <Text variant="capsSm" tone="muted" className="mb-1">
            WARRANTY
          </Text>
          <Text variant="bodyBold" tone="dark" className="mb-1">
            {mostWarranty.name ?? "—"}
          </Text>
          <Text variant="bodyXs" tone="muted">
            {mostWarranty.warranty?.name ?? "—"} warranty coverage
          </Text>
        </div>
        <div className="rounded-xl border border-borderDefault bg-bgLight p-4">
          <Text variant="capsSm" tone="muted" className="mb-1">
            BEST PRICE
          </Text>
          <Text variant="bodyBold" tone="dark" className="mb-1">
            {cheapest ? (cheapest.product.name ?? "—") : "Price unavailable"}
          </Text>
          <Text variant="bodyXs" tone="muted">
            {cheapest ? (
              <GatedPrice>{formatUnitPrice(cheapest.price)}</GatedPrice>
            ) : (
              "Request a quotation for exact pricing"
            )}
          </Text>
        </div>
        <div className="rounded-xl border border-borderDefault bg-bgLight p-4">
          <Text variant="capsSm" tone="muted" className="mb-1">
            AVAILABILITY
          </Text>
          <Text variant="bodyBold" tone="dark" className="mb-1">
            {bestStocked.name ?? "—"}
          </Text>
          <Text variant="bodyXs" tone="muted">
            {Number(bestStocked.supplierStock ?? 0).toLocaleString()} units in stock
          </Text>
        </div>
      </div>
    </div>
  );
}

function formatUnitPrice(price: number): string {
  return `QAR ${price.toLocaleString()} per unit`;
}
